import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { Clock, Check } from "lucide-react";
import { cn } from "../../lib/utils";
import { useAuth } from "../../auth/AuthContext";

import { Service } from "../../types/calendar";
interface Props {
  services: Service[];
  /** Current search text (filtering is done in parent). */
  searchQuery?: string;
  /** Count before search filter — used to show "no match" vs "no services". */
  catalogServiceCount?: number;
  setSelectedService?: (service: Service) => void;
  setStep: (
    step: "selection" | "customer" | "summary" | "payment" | "finalPayment"
  ) => void;
  disabledServices?: Service[];
  isAddingService?: boolean;
  setIsAddingService?: (value: boolean) => void;
  setSelectedServices?: ((services: Service[]) => void) | React.Dispatch<React.SetStateAction<Service[]>>;
  selectedStylistId?: number;
  selectedStylistName?: string;
}

const ServiceSelection: React.FC<Props> = ({
  services,
  searchQuery = "",
  catalogServiceCount,
  setSelectedService,
  setStep,
  disabledServices = [],
  isAddingService = false,
  setIsAddingService,
  setSelectedServices,
  selectedStylistId,
  selectedStylistName,
}) => {
  const { user } = useAuth();

  const currencySymbol = user?.company?.currency_symbol || user?.company?.currency?.currency_symbol || "";

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 },
  };

  const groupedServices = useMemo(() => {
    return services.reduce((acc, service) => {
      const category = service.categoryName || "Uncategorized";
      if (!acc[category]) acc[category] = [];
      acc[category].push(service);
      return acc;
    }, {} as Record<string, Service[]>);
  }, [services]);

  const catalogCount = catalogServiceCount ?? 0;
  const normalizedQuery = (searchQuery || "")
    .replace(/\u200b/g, "")
    .replace(/\u00a0/g, " ")
    .trim();
  const hasActiveSearch = normalizedQuery.length > 0;
  const noMatch =
    services.length === 0 && catalogCount > 0 && hasActiveSearch;

  return (
    <div className="flex flex-col gap-3">
      <motion.div
        key={`svc-list-${services.length}-${(searchQuery || "").length}`}
        variants={containerVariants}
        initial={false}
        animate="visible"
        className="flex flex-col gap-3"
      >
        {noMatch ? (
          <div className="text-center py-10 bg-white rounded-xl border border-dashed border-gray-200">
            <p className="text-xs font-medium text-gray-500">
              {`No services match "${normalizedQuery}"`}
            </p>
          </div>
        ) : services.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-dashed border-gray-200">
            <p className="text-sm text-gray-400 font-medium">
              No services available for this stylist/slot.
            </p>
          </div>
        ) : (
          Object.entries(groupedServices).map(([category, categoryServices]) => (
            <div key={category} className="space-y-3">
              <h3 className="text-[#885ABB] font-sans text-base font-semibold">
                {category} <span className="text-[#885ABB]">({categoryServices.length})</span>
              </h3>
              {categoryServices.map((service) => {
                const isDisabled = disabledServices.some((s) => s.id === service.id);

                return (
                  <motion.div
                    key={service.id}
                    variants={itemVariants}
                    className={cn(
                      "group relative flex items-center justify-between p-5 bg-white border border-gray-100 rounded-xl cursor-pointer transition-all duration-300 shadow-sm",
                      isDisabled ? "opacity-40 cursor-not-allowed grayscale" : ""
                    )}
                    onClick={() => {
                      if (isDisabled) return;

                      const serviceWithStylist: Service = {
                        ...service,
                        assignedStylistId: selectedStylistId,
                        assignedStylistName: selectedStylistName,
                      };

                      if (
                        isAddingService &&
                        setSelectedServices &&
                        setIsAddingService
                      ) {
                        setSelectedServices([...disabledServices, serviceWithStylist]);
                        setStep("summary");
                        setIsAddingService(false);
                      } else if (setSelectedServices) {
                        setSelectedServices([serviceWithStylist]);
                        setStep("customer");
                      } else if (setSelectedService) {
                        setSelectedService(serviceWithStylist);
                        setStep("customer");
                      }
                    }}
                  >
                    <div className="flex-1 min-w-0 pr-4">
                      <h3 className="text-base font-[600] text-[#885ABB] truncate" title={service.name}>
                        {service.name}
                      </h3>
                      <div className="flex items-center gap-2 mt-1 text-gray-400">
                        <Clock size={12} />
                        <span className="text-xs font-medium">{service.duration}</span>
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-1">
                      {isDisabled && (
                        <div className="flex items-center gap-1 opacity-70 mb-1">
                          <Check size={10} className="text-[#885ABB]" strokeWidth={4} />
                          <span className="text-[9px] font-[600] text-[#885ABB] uppercase">Selected</span>
                        </div>
                      )}
                      {selectedStylistId && service.staffIds && !service.staffIds.includes(selectedStylistId) && (
                        <div className="bg-[#885ABB]/10 text-[#885ABB] text-[8px] px-2 py-0.5 rounded-full font-bold uppercase mb-1 whitespace-nowrap">
                          Not provided by stylist
                        </div>
                      )}

                      {(() => {
                        const originalPrice = typeof service.price === "string"
                          ? parseFloat(service.price.replace(/[£$€]/g, ""))
                          : Number(service.price || 0);
                        const discount = parseFloat(String(service.discount)) || 0;
                        const discountType = service.discount_type;

                        if (discount > 0) {
                          let discountedPrice = originalPrice;
                          if (discountType === "percent") {
                            discountedPrice = originalPrice - (originalPrice * discount) / 100;
                          } else {
                            discountedPrice = originalPrice - discount;
                          }
                          discountedPrice = Math.max(0, discountedPrice);

                          return (
                            <div className="flex flex-col items-end">
                              <span className="text-[10px] sm:text-[11px] font-bold text-gray-400 line-through">
                                {currencySymbol}{originalPrice.toFixed(2)}
                              </span>
                              <span className="text-base sm:text-lg font-[700] text-[#885ABB]">
                                {currencySymbol}{discountedPrice.toFixed(2)}
                              </span>
                            </div>
                          );
                        }

                        return (
                          <span className="text-base sm:text-lg font-[600] text-[#885ABB]">
                            {currencySymbol}{originalPrice.toFixed(2)}
                          </span>
                        );
                      })()}
                    </div>

                    <div className={cn(
                      "absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 rounded-r-full transition-colors duration-300",
                      isDisabled ? "bg-gray-300" : "bg-[#885ABB]"
                    )} />
                  </motion.div>
                );
              })}
            </div>
          ))
        )}
      </motion.div>
    </div>
  );
};

export default ServiceSelection;
